/**
 * IndexedDB schema offline (Dexie). Sprint 4.
 *
 * Tablas:
 *   - assessments   : copia local del assessment + sus instances
 *   - photos        : Blob de la foto + metadatos pendientes de upload
 *   - pending       : cola de cambios a sincronizar (responses, complete)
 *   - templates     : cache de schemas de plantilla para render offline
 *
 * Los campos `body` que contienen JSON cifrado guardan
 * `{ iv: Uint8Array, ciphertext: Uint8Array }`. Cuando no hay key
 * AES (sesion sin login completo) los reads fallan deliberadamente.
 */
import Dexie, { type EntityTable } from 'dexie';

export interface LocalAssessment {
  id: string;
  /** Cifrado JSON con shape de AssessmentDetail. */
  iv: Uint8Array;
  ciphertext: Uint8Array;
  updatedAt: number;
}

export interface LocalPhotoQueueItem {
  id: string;
  assessmentId: string;
  instanceId: string;
  questionRef: string;
  /** Bytes cifrados del archivo de imagen. */
  iv: Uint8Array;
  ciphertext: Uint8Array;
  contentType: string;
  capturedAt: number;
  status: 'pending' | 'uploaded' | 'error';
  serverPhotoId?: string;
  lastError?: string;
}

export interface LocalPendingChange {
  clientChangeId: string;
  kind: 'responses' | 'complete';
  assessmentId: string;
  /** Cifrado del payload del change (`responses` o `{}`). */
  iv: Uint8Array;
  ciphertext: Uint8Array;
  ts: number;
  status: 'pending' | 'syncing' | 'error';
  attempts: number;
  lastError?: string;
}

export interface LocalTemplate {
  key: string; // code@version
  /** Cifrado JSON con el schema. */
  iv: Uint8Array;
  ciphertext: Uint8Array;
  cachedAt: number;
}

export class OfflineDB extends Dexie {
  assessments!: EntityTable<LocalAssessment, 'id'>;
  photos!: EntityTable<LocalPhotoQueueItem, 'id'>;
  pending!: EntityTable<LocalPendingChange, 'clientChangeId'>;
  templates!: EntityTable<LocalTemplate, 'key'>;

  constructor() {
    super('assessos-offline');
    this.version(1).stores({
      assessments: 'id, updatedAt',
      photos: 'id, assessmentId, instanceId, status',
      pending: 'clientChangeId, status, ts',
      templates: 'key',
    });
  }
}

export const offlineDB = typeof indexedDB !== 'undefined' ? new OfflineDB() : null;
